import { useMemo, useState } from 'react';
import { FIELD_BINDING_COLLECTIONS } from '@dealstack/shared/lib/documents/fieldBindingCatalogue.ts';
import {
	isCollectionCollected,
	isItemFieldCollected,
} from '@dealstack/shared/lib/documents/programmeDataRequirements.ts';
import type { FieldBindingCollection } from '@dealstack/shared/lib/documents/fieldBindingCatalogue.ts';
import {
	LIST_FIELD_SEPARATORS,
	LIST_ITEM_SEPARATORS,
	buildListLiquidExpression,
} from './bindPanel.utils.ts';
import { renderListPreview, restoreListState, togglePath } from './bindingBuilder.utils.ts';
import type { ListFieldSeparator, ListItemSeparator } from './BindPanel.types.ts';
import type { ListBinding } from './bindingBuilder.types.ts';

const collectionFor = (key: string): FieldBindingCollection =>
	FIELD_BINDING_COLLECTIONS.find((entry) => entry.key === key) ?? FIELD_BINDING_COLLECTIONS[0];

const templateFor = (parts: {
	collection: FieldBindingCollection;
	itemPaths: string[];
	fieldSeparator: ListFieldSeparator;
	itemSeparator: ListItemSeparator;
}): string =>
	buildListLiquidExpression({
		source: parts.collection.source,
		itemPaths: parts.itemPaths,
		fieldSeparator: LIST_FIELD_SEPARATORS[parts.fieldSeparator],
		itemSeparator: LIST_ITEM_SEPARATORS[parts.itemSeparator],
	});

/**
 * List mode: a "for each" over one collection, printing chosen item fields per row.
 *
 * Every control writes through one `emit`, so they cannot drift into building the template
 * differently from one another.
 */
export const useListBinding = ({
	bindingTemplate,
	uncollectedRequirements,
	onChange,
}: {
	bindingTemplate: string | null;
	uncollectedRequirements: readonly string[];
	onChange: (bindingTemplate: string) => void;
}): ListBinding => {
	const initial = useMemo(() => restoreListState(bindingTemplate), [bindingTemplate]);
	const [collectionKey, setCollectionKey] = useState(initial.collectionKey);
	const [itemPaths, setItemPaths] = useState<string[]>(initial.itemPaths);
	const [fieldSeparator, setFieldSeparator] = useState<ListFieldSeparator>(
		initial.fieldSeparator
	);
	const [itemSeparator, setItemSeparator] = useState<ListItemSeparator>(initial.itemSeparator);

	const collection = collectionFor(collectionKey);
	const current = { collection, itemPaths, fieldSeparator, itemSeparator };

	const collections = useMemo(
		() =>
			FIELD_BINDING_COLLECTIONS.filter(
				(entry) =>
					entry.key === collection.key ||
					isCollectionCollected(entry, uncollectedRequirements)
			),
		[collection, uncollectedRequirements]
	);
	const offeredItemFields = useMemo(
		() =>
			collection.itemFields.filter((itemField) =>
				isItemFieldCollected(collection, itemField, uncollectedRequirements)
			),
		[collection, uncollectedRequirements]
	);

	const template = useMemo(
		() => templateFor({ collection, itemPaths, fieldSeparator, itemSeparator }),
		[collection, itemPaths, fieldSeparator, itemSeparator]
	);
	// An empty selection has no sentence to show, so the preview line is omitted rather than
	// rendering an empty loop.
	const preview = useMemo(
		() => (itemPaths.length === 0 ? '' : renderListPreview(template)),
		[template, itemPaths]
	);

	const emit = (parts: Partial<typeof current> = {}) =>
		onChange(templateFor({ ...current, ...parts }));

	const applyItemPaths = (next: string[]) => {
		setItemPaths(next);
		emit({ itemPaths: next });
	};

	return {
		collection,
		collections,
		offeredItemFields,
		itemPaths,
		fieldSeparator,
		itemSeparator,
		preview,
		onChangeCollection: (key) => {
			const next = collectionFor(key);
			// Item paths are collection-specific and would not resolve against another list,
			// so switching collection clears them.
			setCollectionKey(next.key);
			setItemPaths([]);
			emit({ collection: next, itemPaths: [] });
		},
		onToggleItemField: (path) => applyItemPaths(togglePath(itemPaths, path)),
		onRemoveItemField: (path) =>
			applyItemPaths(itemPaths.filter((selected) => selected !== path)),
		onChangeFieldSeparator: (next) => {
			setFieldSeparator(next);
			emit({ fieldSeparator: next });
		},
		onChangeItemSeparator: (next) => {
			setItemSeparator(next);
			emit({ itemSeparator: next });
		},
		apply: () => emit(),
	};
};
